import { getOfflineStorage, OfflineStorage } from './storage';

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

export const AUTO_LOGIN_LIFETIME_DAYS = 30;
export const OFFLINE_SESSION_RESUME_HOURS = 12;
export const AUTH_CACHE_VERSION = 2;

export function credentialKey(userId: string) {
    return `credential:${userId}`;
}

export function toBool(value: any): boolean {
    return value === true || ['1', 'true', 'y', 'yes', 'on'].includes(String(value || '').toLowerCase());
}

export function authExpiresAt(now: number = Date.now()): number {
    return now + AUTO_LOGIN_LIFETIME_DAYS * DAY_MS;
}

export function resolveCachedUserId(user: any): string {
    if (!user || typeof user !== 'object') return '';
    const recordId = user.id && user.id !== 'current_user' ? user.id : '';
    return String(user.user_id || user.login_id || recordId || '').trim();
}

export function normalizeSessionUser(userId: string, data: any) {
    const source = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    const session = { ...source };
    const resolvedUserId = session.user_id || session.id || userId;
    session.user_id = resolvedUserId;
    session.login_id = userId || resolvedUserId;
    return session;
}

export function createSessionCacheRecord(userId: string, data: any, options: any = {}) {
    const now = Number(options.now || Date.now());
    const existing = options.existing && typeof options.existing === 'object' ? options.existing : {};
    const autoLogin = options.autoLogin !== undefined ? toBool(options.autoLogin) : toBool(existing.auto_login_enabled);
    const expiresAt = autoLogin
        ? Number(options.autoLoginExpiresAt || existing.auto_login_expires_at || authExpiresAt(now))
        : 0;
    const session = normalizeSessionUser(userId, data);
    const authenticatedAt = Number(
        options.authenticatedAt
        || session.authenticated_at
        || existing.authenticated_at
        || (options.online === false ? 0 : now)
    );

    return {
        ...session,
        id: 'current_user',
        auth_cache_version: AUTH_CACHE_VERSION,
        auto_login_enabled: autoLogin,
        auto_login_expires_at: expiresAt,
        cached_at: now,
        authenticated_at: authenticatedAt,
        last_online_at: options.online === false ? existing.last_online_at : now,
        auth_mode: options.authMode || existing.auth_mode || 'server',
        offline: options.offline === true
    };
}

export function createCredentialCacheRecord(userId: string, passwordDigest: string, session: any, options: any = {}) {
    const now = Number(options.now || Date.now());
    const autoLogin = toBool(options.autoLogin);
    const serverPasswordHash = autoLogin ? String(options.serverPasswordHash || '') : '';
    return {
        id: credentialKey(userId),
        user_id: userId,
        password_digest: passwordDigest,
        server_password_hash: serverPasswordHash,
        session: normalizeSessionUser(userId, session),
        auth_cache_version: AUTH_CACHE_VERSION,
        auto_login_enabled: autoLogin,
        auto_login_expires_at: autoLogin ? authExpiresAt(now) : 0,
        expires_at: authExpiresAt(now),
        cached_at: now
    };
}

export function isCredentialUsable(record: any, now: number = Date.now()): boolean {
    if (!record || !record.password_digest) return false;
    const expiresAt = Number(record.expires_at || 0);
    if (expiresAt > 0) return expiresAt > now;
    const cachedAt = Number(record.cached_at || 0);
    return cachedAt > 0 && cachedAt + AUTO_LOGIN_LIFETIME_DAYS * DAY_MS > now;
}

export function isAutoLoginUsable(record: any, now: number = Date.now()): boolean {
    if (!resolveCachedUserId(record)) return false;
    if (!toBool(record.auto_login_enabled)) return false;
    return Number(record.auto_login_expires_at || 0) > now;
}

export function isOfflineSessionResumeUsable(record: any, now: number = Date.now()): boolean {
    if (!resolveCachedUserId(record)) return false;
    if (record.offline !== true) return false;
    const authenticatedAt = Number(record.authenticated_at || 0);
    return authenticatedAt > 0 && authenticatedAt + OFFLINE_SESSION_RESUME_HOURS * HOUR_MS > now;
}

export function isRecentOnlineSessionResumeUsable(record: any, now: number = Date.now()): boolean {
    if (!resolveCachedUserId(record)) return false;
    const lastOnlineAt = Number(record.last_online_at || record.authenticated_at || record.cached_at || 0);
    return lastOnlineAt > 0 && lastOnlineAt + OFFLINE_SESSION_RESUME_HOURS * HOUR_MS > now;
}

export function sessionDataFromCache(record: any, options: any = {}) {
    if (!record) return null;
    const userId = resolveCachedUserId(record);
    if (!userId) return null;
    const { id: _, ...session } = record;
    return {
        ...session,
        id: userId,
        user_id: userId,
        login_id: record.login_id || userId,
        auth_mode: options.authMode || record.auth_mode || 'cache',
        offline: options.offline !== undefined ? options.offline : record.offline === true
    };
}

export async function getCachedSessionRecord(storage: OfflineStorage): Promise<any | null> {
    return storage.get('auth', 'current_user');
}

export async function getCachedSessionData(storage: OfflineStorage): Promise<any | null> {
    const record = await getCachedSessionRecord(storage);
    return sessionDataFromCache(record, { authMode: 'cache', offline: record?.offline === true });
}

export async function restoreCachedAutoLoginSession(allowOfflineSession: boolean = false): Promise<any | null> {
    const storage = getOfflineStorage();
    const record = await getCachedSessionRecord(storage);
    const canRestoreOffline = allowOfflineSession && (
        isOfflineSessionResumeUsable(record)
        || isRecentOnlineSessionResumeUsable(record)
    );
    if (!isAutoLoginUsable(record) && !canRestoreOffline) return null;
    return sessionDataFromCache(record, { authMode: 'cache', offline: true });
}
